<template>
  <div class="tw-flex tw-flex-col tw-gap-5 tw-justify-between tw-w-full">
    <div class="tw-flex tw-w-full tw-h-11 tw-items-center tw-justify-between">
      <span
        id="chr-sheading-forecast"
        class="tw-text-[#211F58] tw-font-semibold tw-text-[24px]"
      >
        {{
          getComponentUiText(
            "app-share-cyber-profile-security-stability-score-title-1",
          )
        }}
      </span>
      <div
        v-if="!isHuggingChart"
        id="expand-icon"
        :class="isHuggingChart ? 'tw-bg-[#211F58]' : 'tw-bg-[#FFF]'"
        class="btn__expand tw-cursor-pointer"
        @click="handleExpandChart"
      >
        <ExpandIcon :color="isHuggingChart ? '#ffffff' : '#387DFF'" />
      </div>
      <div
        v-else
        id="expand-icon"
        :class="isHuggingChart ? 'tw-bg-[#211F58]' : 'tw-bg-[#FFF]'"
        class="btn__expand tw-cursor-pointer"
        @click="handleExpandChart"
      >
        <ExpandIcon :color="isHuggingChart ? '#ffffff' : '#387DFF'" />
      </div>
    </div>
    <div
      class="tw-flex tw-w-full tw-h-[304px] tw-border tw-border-solid tw-border-[#EFF0F1] tw-rounded-md tw-px-[10px] tw-py-[15px]"
    >
      <div
        v-if="isLoading"
        class="custom-global-skeleton tw-w-full tw-h-full"
      />
      <LineChart
        v-else
        :chart-id="chartId"
        :chart-options="cloneDeep(chartOptions)"
        :series="series"
        height="100%"
        :is-print="props.isPrint"
        :is-change-size="true"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import LineChart from "@/components/chart/lineChart.vue";
import { getComponentUiText } from "@/utils/utilities";
import { onMounted, ref, watch } from "vue";
import {
  connectNulls,
  getDataToChart,
  getMarkers,
  removeDuplicateMonthYear,
} from "@/utils/apexChart";
import { convertUTCTimeToLocalTimeAndFormat } from "@/utils/dateTimeUtils";
import ExpandIcon from "../svgs/expandIcon.vue";
import cloneDeep from "lodash/cloneDeep";

type TProps = {
  isLoading?: boolean;
  isPrint?: boolean;
  data?: {
    checklistHistory: {
      date: string;
      score: number;
      isLatestHistory?: boolean;
    }[];
    checklistForecasts: {
      date: string;
      score: number;
    }[];
  };
};

const props = defineProps<TProps>();

const series = ref<any[]>([]);
const chartOptions = ref<any>({});
const isHuggingChart = ref<boolean>(false);
const chartId = ref<number>(1);
const minHistoryScore = ref<number>(0);
const maxHistoryScore = ref<number>(0);
const minChartValue = ref<number>(0);
const maxChartValue = ref<number>(0);
const chartWidth = ref<string | number>("100%");

onMounted(() => {
  initialData();
});

watch(
  () => props.data,
  () => {
    initialData();
  },
);

const initialData = () => {
  let result = {} as any;
  const checklistHistory = removeDuplicateMonthYear(
    props.data?.checklistHistory || [],
    ["date"],
  );
  if (checklistHistory.length) {
    checklistHistory[checklistHistory.length - 1].isLatestHistory = true;
  }
  minHistoryScore.value = Math.min(
    ...checklistHistory.map((checklist) => checklist.score),
  );
  maxHistoryScore.value = Math.max(
    ...checklistHistory.map((checklist) => checklist.score),
  );
  const avgScore = (minHistoryScore.value + maxHistoryScore.value) / 2;

  const checklistForecasts = removeDuplicateMonthYear(
    props.data?.checklistForecasts || [],
    ["date"],
  );
  minChartValue.value = Math.min(
    ...checklistHistory.map((item) => Number(item.score) * 100),
    ...checklistForecasts.map((item) => Number(item.score) * 100),
  );
  maxChartValue.value = Math.max(
    ...checklistHistory.map((item) => Number(item.score) * 100),
    ...checklistForecasts.map((item) => Number(item.score) * 100),
  );

  result = getDataToChart(
    [...checklistHistory, ...checklistForecasts],
    "date",
    "score",
  );

  const historyValues = [...result.valueArrays].fill(
    -1,
    result.latestHistoryIdx + 1,
  );
  const historyMarkers = getMarkers(
    historyValues,
    connectNulls([...historyValues])[0].data,
    false,
    0,
    false,
  );
  const historySeriesData = connectNulls([...historyValues], true);

  const forecastValues = [...result.valueArrays].fill(
    -1,
    0,
    result.latestHistoryIdx,
  );
  const forecastMarkers = forecastValues.map((value: any, index: number) => ({
    dataPointIndex: index,
    fillColor: "#c7c7c7",
    seriesIndex: 1,
    shape: "circle",
    size: !value || index === result.latestHistoryIdx ? 0 : 6,
    strokeColor: "#c7c7c7",
  }));
  const forecastSeriesData = connectNulls([...forecastValues], true);

  series.value = [
    {
      name: "checklistHistory",
      data: historySeriesData[0].data.map((score) =>
        score === null ? null : Number(score) * 100,
      ),
    },
    {
      name: "checklistForecast",
      data: forecastSeriesData[0].data.map((score) =>
        score === null ? null : Number(score) * 100,
      ),
    },
  ];
  const mergedMarkers = [
    ...historyMarkers.slice(0, result.latestHistoryIdx + 1),
    ...forecastMarkers.slice(result.latestHistoryIdx + 1),
  ];

  chartOptions.value = {
    chart: {
      height: "100%",
      type: "line",
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      },
    },
    tooltip: {
      custom: ({ dataPointIndex }) =>
        result.valueArrays?.[dataPointIndex] !== -1
          ? tooltipCustom(
              dataPointIndex,
              result.monthArr,
              result.valueArrays,
              mergedMarkers,
            )
          : "",
    },
    legend: {
      show: false,
    },
    markers: {
      size: 6,
      colors: ["#211F58", "#c7c7c7"],
      strokeColors: ["#211F58", "#c7c7c7"],
      strokeWidth: [2.5, 0],
      fillOpacity: 1,
      strokeOpacity: 1,
      discrete: [...historyMarkers, ...forecastMarkers],
      hover: {
        size: 6,
      },
    },
    states: {
      hover: {
        filter: {
          type: "none",
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "straight",
      lineCap: "butt",
      colors: ["#c7c7c7"],
      width: [3, 3],
      dashArray: [0, 5],
    },
    xaxis: {
      categories: result.monthArr,
      labels: {
        formatter: (value: string) => {
          return result.mainTicks?.[value]
            ? convertUTCTimeToLocalTimeAndFormat({
                utcTime: value,
                customParseFormat: "M-YYYY",
              }).format("MMM 'YY")
            : "";
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (value: string) => `${value}%`,
      },
    },
    grid: {
      padding: {
        left: 10,
        right: 20,
      },
    },
    fill: {
      type: ["gradient", "solid"],
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        type: "vertical",
        inverseColors: true,
        colorStops: [
          {
            offset: 0,
            color: "#01893D",
            opacity: 1,
          },
          {
            offset: avgScore * 100,
            color: "#FFA300",
            opacity: 1,
          },
          {
            offset: 100,
            color: "#F70000",
            opacity: 1,
          },
        ],
      },
    },
  };
};

const tooltipCustom = (dataPointIndex, xAxis, yAxis, markers) => {
  return `<div
      class="apexcharts-tooltip-title"
      style="font-family: Helvetica, Arial, sans-serif; font-size: 12px"
    >
      ${
        xAxis[dataPointIndex]
          ? convertUTCTimeToLocalTimeAndFormat({
              utcTime: xAxis[dataPointIndex],
              customParseFormat: "M-YYYY",
            }).format("MMM-YYYY")
          : ""
      }
    </div>
    <div
      class="apexcharts-tooltip-series-group apexcharts-active"
      style="order: 1; display: flex"
    >
      <span
        class="apexcharts-tooltip-marker"
        style="background-color: ${markers?.[dataPointIndex]?.strokeColor}"
      ></span>
      <div
        class="apexcharts-tooltip-text"
        style="font-family: Helvetica, Arial, sans-serif; font-size: 12px"
      >
        <div class="apexcharts-tooltip-y-group">
          <span class="apexcharts-tooltip-text-y-label" >${
            yAxis[dataPointIndex] !== null
              ? getComponentUiText(
                  "app-component-assessments-question-type-2-icon-tooltip-score-1",
                ) + ": "
              : ""
          }</span
          ><span class="apexcharts-tooltip-text-y-value">${
            yAxis[dataPointIndex] === null
              ? getComponentUiText("app-share-security-chart-no-data-1")
              : `${yAxis[dataPointIndex] * 100}%`
          }</span>
        </div>
        <div class="apexcharts-tooltip-goals-group">
          <span class="apexcharts-tooltip-text-goals-label"></span
          ><span class="apexcharts-tooltip-text-goals-value"></span>
        </div>
        <div class="apexcharts-tooltip-z-group">
          <span class="apexcharts-tooltip-text-z-label"></span
          ><span class="apexcharts-tooltip-text-z-value"></span>
        </div>
      </div>
    </div>`;
};

// const generateHistoryColorStops = () => {
//   const colorStops: any[] = [];
//   let minScore = minHistoryScore.value,
//     maxScore = maxHistoryScore.value;

//   if (series.value[0]?.data?.includes(null) && !isHuggingChart.value) {
//     minScore = 0;
//   }
//   for (let i = 0; i <= 100; i += 10) {
//     const value = ((100 - i) / 100) * (maxScore - minScore) + minScore;
//     colorStops.push({
//       offset: i,
//       color: getColorGradients(value, 0, 1, false),
//       opacity: 1,
//     });
//   }
//   return colorStops;
// };

const handleExpandChart = () => {
  isHuggingChart.value = !isHuggingChart.value;
  if (isHuggingChart.value) {
    let before = Math.floor(minChartValue.value / 10) * 10;
    let after = Math.ceil(maxChartValue.value / 10) * 10;
    chartOptions.value.yaxis.min = before;
    chartOptions.value.yaxis.max = after;
  } else {
    chartOptions.value.yaxis.min = 0;
    chartOptions.value.yaxis.max = 100;
  }
  chartId.value++;
};
</script>

<style lang="scss" scoped>
.btn__expand {
  border-radius: 6px;
  border: 1px solid var(--UI-rule, #eff0f1);
  padding: 9px 14px;
  box-shadow: 0px 0px 4px 0px rgba(0, 0, 0, 0.05);
  width: 46px;
  height: 44px;
}

:deep(.container) {
  overflow: visible;
}

@media print {
  #expand-icon {
    display: none;
  }
}
</style>
